import { FieldSet, NumberInput, Slider } from '@wix/design-system';
import React, { useContext, useRef, useState } from 'react';

import { ZIndexContext } from './../../ricos-context';
import type { LogBiFn } from './../../_shared/bi-context-provider/bi-context-provider';

type SliderWithInputProps = {
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: React.ReactNode;
  sliderDataHook: string;
  inputDataHook: string;
  value: number;
  tooltipTextKey?: string;
  tooltipAppendTo?: HTMLElement;
  disabled?: boolean;
  normalizeMaxValue?: boolean;
  logBi?: LogBiFn; // TODO: make this required prop
  suffix?: React.ReactNode;
};

export const SliderWithInput: React.FC<SliderWithInputProps> = ({
  min,
  max,
  onChange,
  label,
  sliderDataHook,
  inputDataHook,
  value,
  tooltipTextKey,
  tooltipAppendTo,
  disabled,
  normalizeMaxValue = true,
  logBi,
  suffix,
}) => {
  const zIndexService = useContext(ZIndexContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputKey, setInputKey] = useState(0);
  const [inputValue, setInputValue] = useState<string | number>(value);

  React.useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputWithinBounds = (data: string | undefined) => {
    const numberValue = Math.round(Number(data));

    const valueWithinBounds =
      isNaN(numberValue) || numberValue < min
        ? min
        : normalizeMaxValue && numberValue > max
        ? max
        : numberValue;

    setInputValue(valueWithinBounds);

    if (value !== valueWithinBounds) {
      onChange(valueWithinBounds);
      return;
    }

    setInputKey(prev => prev + 1);
  };

  return (
    <FieldSet
      legend={label}
      legendSize="small"
      legendPlacement="top"
      columns={suffix ? 'auto 72px' : 'auto 60px'}
      infoContent={tooltipTextKey}
      infoTooltipProps={{
        appendTo:
          tooltipAppendTo ??
          (el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal'))),
        zIndex: zIndexService.getZIndex('TOOLTIP'),
      }}
    >
      <Slider
        disabled={disabled}
        onChange={value => {
          const sliderValue = typeof value === 'number' ? value : value[0];
          logBi?.({ dataHook: sliderDataHook, value: sliderValue });
          onChange(sliderValue);
          setInputValue(sliderValue);
        }}
        min={min}
        max={max}
        value={value}
        displayMarks={false}
        dataHook={sliderDataHook}
        displayTooltip={false}
      />
      <NumberInput
        disabled={disabled}
        key={inputKey}
        inputRef={inputRef}
        value={inputValue}
        min={min}
        max={max}
        onChange={(value, stringValue) => {
          if (stringValue === '') {
            setInputValue('');
            return;
          }

          if (typeof value === 'number') {
            logBi?.({ dataHook: inputDataHook, value });
            onChange(value);
            setInputValue(value);
          }
        }}
        size="small"
        hideStepper
        dataHook={inputDataHook}
        onBlur={e => handleInputWithinBounds(e.target.value)}
        onKeyDown={e => {
          if (['Enter', 'Esc'].includes(e.key)) {
            handleInputWithinBounds(inputRef.current?.value);
          }
        }}
        suffix={suffix}
      />
    </FieldSet>
  );
};
